/**
 * "O valor esperado para uma medida numa certa idade", calculado a partir dos
 * animais medidos.
 *
 * Metodo: media movel ponderada pela idade (kernel gaussiano). Cada animal da
 * populacao pesa mais quanto mais perto estiver da idade pedida — em palavras
 * simples, "a media dos animais da mesma idade". Funciona com poucos dados,
 * da uma curva suave e se explica em uma frase no laudo.
 *
 * Machos e femeas usam a mesma curva por enquanto: com a base atual nao ha dado
 * para separar sem que cada curva fique com meia duzia de pontos. Quando houver,
 * a separacao e so filtrar os pontos antes de chamar.
 *
 * Nunca inventa: longe dos dados (idade sem animais medidos por perto) ou com
 * poucos animais efetivos, devolve `null`, e a tela diz "sem referencia".
 */

export interface PontoDeReferencia {
  meses: number;
  valor: number;
}

export interface Esperado {
  media: number;
  /** Desvio-padrao ponderado: a faixa normal e media ± dp. */
  dp: number;
  /** Numero efetivo de animais que sustentam a estimativa naquela idade. */
  n: number;
}

/** Largura do kernel, em meses. */
export const LARGURA_MESES = 6;
/** Abaixo disso de animais efetivos, nao ha referencia. */
export const MINIMO_EFETIVO = 3;

export interface CurvaDeReferencia {
  esperadoEm(meses: number): Esperado | null;
}

export function curvaDeReferencia(
  pontos: readonly PontoDeReferencia[],
  { largura = LARGURA_MESES, minimo = MINIMO_EFETIVO }: { largura?: number; minimo?: number } = {},
): CurvaDeReferencia {
  return {
    esperadoEm(meses) {
      // Janela dura de 2 larguras: sem isso a curva "extrapola" com o peso
      // residual de animais muito mais velhos ou mais novos.
      const perto = pontos.filter((p) => Math.abs(p.meses - meses) <= 2 * largura);
      if (perto.length === 0) return null;
      const pesos = perto.map((p) => Math.exp(-0.5 * ((p.meses - meses) / largura) ** 2));
      const soma = pesos.reduce((a, b) => a + b, 0);
      const somaQuadrados = pesos.reduce((a, b) => a + b * b, 0);
      // Kish: quantos animais "de verdade" os pesos representam.
      const n = (soma * soma) / somaQuadrados;
      if (n < minimo) return null;
      const media = perto.reduce((a, p, i) => a + pesos[i]! * p.valor, 0) / soma;
      const variancia = perto.reduce((a, p, i) => a + pesos[i]! * (p.valor - media) ** 2, 0) / soma;
      // Correcao de amostra pequena, pelo n efetivo.
      const dp = Math.sqrt((variancia * n) / (n - 1));
      return { media, dp, n };
    },
  };
}

export type Leitura = 'abaixo' | 'dentro' | 'acima';

export interface Desvio {
  diferenca: number;
  /** Em desvios-padrao da populacao naquela idade. */
  z: number;
  leitura: Leitura;
}

/** Onde o valor cai em relacao ao esperado. "Dentro" e ate 1 desvio-padrao. */
export function desvioDoEsperado(valor: number, ref: Esperado): Desvio {
  const diferenca = valor - ref.media;
  const z = ref.dp > 0 ? diferenca / ref.dp : 0;
  return { diferenca, z, leitura: z > 1 ? 'acima' : z < -1 ? 'abaixo' : 'dentro' };
}
